/**
 * Store credit as a trade-in payout (Your account, 4 Nov 2026), as the staff
 * pages show it.
 *
 * Prism strikes both figures and owns the rounding: the cash figure is the buy
 * price and the credit figure is what the seller is paid in Shopify store credit.
 * This file only picks the figures apart and words them. It never computes one.
 */

/** Why Prism cannot issue the credit, in words for staff. */
export const CANNOT_ISSUE_WORDS = Object.freeze({
  no_shopify_account:
    'The seller has no signed-in Audio Trader account, so there is no Shopify customer to credit. Pay them in cash instead.',
  switched_off: 'Store credit is switched off.',
  not_a_trade_in: 'Store credit is for trade-ins only.',
})

/** "10%" from basis points; null when Prism sent none. */
export function upliftPercent(bps) {
  if (!Number.isFinite(bps)) return null
  return `${Number((bps / 100).toFixed(2))}%`
}

/**
 * The figures from a preflight's `store_credit` block (pence), or null.
 * `canIssue` is false with a `reason` when Prism will refuse to issue.
 */
export function figuresFromPreflight(facts) {
  if (!facts || !facts.chosen) return null
  return {
    cashPounds: facts.cash_cents == null ? null : facts.cash_cents / 100,
    creditPounds: facts.credit_cents == null ? null : facts.credit_cents / 100,
    percent: upliftPercent(facts.uplift_bps),
    canIssue: facts.can_issue === true,
    reason: facts.reason ?? null,
  }
}

/**
 * The figures from a deal row once the payment is initiated (pounds as strings,
 * the shape the deal projection carries), or null before that.
 */
export function figuresFromDeal(deal) {
  const cash = parseFloat(deal?.store_credit_cash_amount)
  const credit = parseFloat(deal?.store_credit_credit_amount)
  if (!Number.isFinite(cash) || !Number.isFinite(credit)) return null
  return {
    cashPounds: cash,
    creditPounds: credit,
    percent: upliftPercent(deal.store_credit_uplift_bps),
    canIssue: true,
    reason: null,
    issuedAt: deal.store_credit_issued_at ?? null,
  }
}

/** Whether a deal row is a store credit seller. */
export function isStoreCreditDeal(deal) {
  return deal?.payout_choice === 'store_credit' || deal?.paymentMethod === 'store_credit'
}
